import { useState } from 'react';
import { X } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createUser, setUserSiteAccess } from '../../services/adminApi';
import { SiteAccessChecklist } from './SiteAccessChecklist';

interface InviteUserModalProps {
  open: boolean;
  onClose: () => void;
}

const ASSIGNABLE_ROLES = [
  'customer_admin',
  'portfolio_manager',
  'site_operator',
  'analyst',
  'site_viewer',
];

export function InviteUserModal({ open, onClose }: InviteUserModalProps) {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [role, setRole] = useState('site_viewer');
  const [password, setPassword] = useState('');
  const [siteIds, setSiteIds] = useState<string[]>([]);
  const [error, setError] = useState('');
  // #297: when the user row was created but the welcome email failed to send,
  // keep the modal open and force the operator to acknowledge before continuing.
  const [emailWarning, setEmailWarning] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async () => {
      const res = await createUser({
        email,
        password,
        first_name: firstName || undefined,
        last_name: lastName || undefined,
        company_name: companyName || undefined,
        phone_number: phoneNumber || undefined,
        role,
      });
      if (siteIds.length > 0 && res.data?.user_id) {
        await setUserSiteAccess(res.data.user_id, siteIds);
      }
      return res;
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      // The user row was created either way (HTTP 201) — only divergence is the email outcome.
      if (res?.email && res.email.sent === false) {
        setEmailWarning(
          `User ${email} was created, but the welcome email failed to send: ${res.email.error}. ` +
          `Use "Reset Password" on the user row and share the new password out-of-band.`,
        );
        // Stay open — operator must acknowledge before continuing.
      } else {
        onClose();
        resetForm();
      }
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Failed to create user');
    },
  });

  const resetForm = () => {
    setEmail(''); setFirstName(''); setLastName(''); setCompanyName(''); setPhoneNumber('');
    setRole('site_viewer'); setPassword(''); setSiteIds([]); setError(''); setEmailWarning(null);
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <div className="bg-ems-bg-card rounded-2xl border border-ems-border w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-ems-border">
          <h2 className="text-lg font-semibold text-ems-text-primary">
            {emailWarning ? 'User Created — Invite Email Failed' : 'Invite User'}
          </h2>
          <button onClick={handleClose} className="p-1 rounded hover:bg-ems-bg-card-hover text-ems-text-muted"><X className="w-5 h-5" /></button>
        </div>

        {emailWarning ? (
          <div className="p-6 space-y-4">
            <div className="p-3 rounded-lg bg-ems-amber/10 border border-ems-amber/30 text-ems-text-primary text-sm">
              {emailWarning}
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 rounded-lg bg-ems-forest text-ems-green border border-ems-green/20 text-sm font-medium hover:bg-ems-forest/80"
              >
                Acknowledge & Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="p-6 space-y-4">
            {error && <div className="p-3 rounded-lg bg-ems-red/10 border border-ems-red/20 text-ems-red text-sm">{error}</div>}

            <div className="grid grid-cols-2 gap-3">
              <Field label="First Name" value={firstName} onChange={setFirstName} />
              <Field label="Last Name" value={lastName} onChange={setLastName} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Company Name" value={companyName} onChange={setCompanyName} />
              <Field label="Phone Number" value={phoneNumber} onChange={setPhoneNumber} type="tel" />
            </div>
            <Field label="Email" value={email} onChange={setEmail} type="email" required />
            <Field label="Password" value={password} onChange={setPassword} type="password" required />

            <div>
              <label className="block text-sm font-medium text-ems-text-primary mb-1">Role</label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-ems-border text-sm bg-ems-bg text-ems-text-primary outline-none focus:border-ems-green"
              >
                {ASSIGNABLE_ROLES.map((r) => (
                  <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-ems-text-primary mb-1">Site Access</label>
              <SiteAccessChecklist selectedSiteIds={siteIds} onChange={setSiteIds} />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={handleClose} className="px-4 py-2 rounded-lg border border-ems-border text-sm text-ems-text-secondary hover:bg-ems-bg-card-hover">Cancel</button>
              <button type="submit" disabled={mutation.isPending} className="px-4 py-2 rounded-lg bg-ems-forest text-ems-green border border-ems-green/20 text-sm font-medium hover:bg-ems-forest/80 disabled:opacity-50">
                {mutation.isPending ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', required = false }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-ems-text-primary mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full px-3 py-2 rounded-lg border border-ems-border text-sm text-ems-text-primary bg-ems-bg outline-none focus:border-ems-green focus:ring-2 focus:ring-ems-green/20"
      />
    </div>
  );
}
